import {
    AppointmentDailyHuddleFieldsFragment,
    AppointmentFieldsFragment,
    AppointmentStatus,
    ClientFieldsFragment,
    ClinicApptStatusFieldsFragment,
    ClinicFieldsFragment,
    PatientFieldsFragment,
} from '~graphql/generated/graphql'
import { getClientFullName } from './helpers'
import { formatPhoneReadable } from './phone'

export const isAppointmentValidForSchedule = <
    T extends {
        status: AppointmentStatus
        clinic_appt_status?:
            | Pick<ClinicApptStatusFieldsFragment, 'status'>
            | null
            | undefined
        is_time_block?: boolean | null | undefined
        clinician?: any | undefined
        room?: any | undefined
    },
>(
    appointment: T,
    includePending?: boolean,
): boolean => {
    const status = getAppointmentStatusFromAppointment(appointment)

    const validStatuses = [
        AppointmentStatus.Booked,
        AppointmentStatus.Confirmed,
        AppointmentStatus.Completed,
    ]

    if (includePending) {
        validStatuses.push(AppointmentStatus.Pending)
    }

    return (
        !appointment.is_time_block &&
        !!(appointment.clinician || appointment.room) &&
        validStatuses.includes(status)
    )
}

export const filterActiveAppointments = <
    T extends {
        status: AppointmentStatus
        clinic_appt_status?:
            | Pick<ClinicApptStatusFieldsFragment, 'status'>
            | null
            | undefined
    },
>(
    appointments: T[],
    {
        includePending,
        includeNoShow,
    }: {
        includePending?: boolean
        includeNoShow?: boolean
    } = {},
): T[] => {
    const activeStatuses = [
        AppointmentStatus.Booked,
        AppointmentStatus.Confirmed,
        AppointmentStatus.Completed,
    ]

    if (includePending) {
        activeStatuses.push(AppointmentStatus.Pending)
    }

    if (includeNoShow) {
        activeStatuses.push(AppointmentStatus.NoShow)
    }

    return appointments.filter(a => {
        const status = getAppointmentStatusFromAppointment(a)

        return activeStatuses.includes(status)
    })
}

export const getAppointmentStatusFromAppointment = (
    appointment: Pick<AppointmentFieldsFragment, 'status'> & {
        clinic_appt_status?:
            | Pick<ClinicApptStatusFieldsFragment, 'status'>
            | null
            | undefined
    },
): AppointmentStatus => {
    if (appointment.status === AppointmentStatus.Confirmed) {
        return AppointmentStatus.Confirmed
    }

    return appointment.clinic_appt_status?.status || appointment.status
}

export const getIsAppointmentReviewedManually = (
    appointment: Pick<AppointmentFieldsFragment, 'reason_for_cancellation'>,
): boolean => {
    return (
        appointment.reason_for_cancellation?.includes(
            'Reviewed and entered manually',
        ) ||
        appointment.reason_for_cancellation?.includes('Marked as resolved') ||
        false
    )
}

export const getWidgetSourceFromAppointment = (
    appointment: Pick<AppointmentFieldsFragment, 'widget' | 'from_widget'>,
): string | null | undefined =>
    appointment.widget?.widget_name ||
    (appointment.from_widget === 'default_portal' ? 'Default Portal' : '')

export const getClientNameFromAppointment = (
    appointment: Pick<
        AppointmentFieldsFragment,
        'client_pms_id' | 'new_client_name'
    > & {
        client?:
            | Pick<ClientFieldsFragment, 'first_name' | 'last_name'>
            | null
            | undefined
    },
): string => {
    if (appointment.client?.first_name || appointment.client?.last_name) {
        return getClientFullName(appointment.client) || ''
    }

    if (appointment.client_pms_id) {
        if (appointment.client) {
            return 'Unknown Name'
        } else {
            return 'Client not synced'
        }
    }

    return appointment.new_client_name || 'No client'
}

export const getClientPhoneFromAppointment = (
    appointment: Pick<AppointmentFieldsFragment, 'new_client_phone'> & {
        clinic?: Pick<ClinicFieldsFragment, 'address'> | null | undefined
        client?:
            | Pick<ClientFieldsFragment, 'phone' | 'address'>
            | null
            | undefined
    },
    format?: boolean,
): string | null | undefined => {
    const rawPhone = appointment.client?.phone || appointment.new_client_phone
    return format && rawPhone
        ? formatPhoneReadable(
              rawPhone,
              appointment?.client?.address?.country ||
                  appointment?.clinic?.address?.country,
          )
        : rawPhone
}

export const getPatientNameFromAppointment = (
    appointment: Pick<
        AppointmentFieldsFragment,
        'patient_pms_id' | 'new_patient_name'
    > & {
        patient?: Pick<PatientFieldsFragment, 'name'> | null | undefined
    },
): string | null | undefined => {
    if (appointment.patient?.name) {
        return appointment.patient.name
    }

    if (appointment.patient_pms_id) {
        return 'Patient not synced'
    }

    return appointment.new_patient_name || 'No patient'
}

export const getSpeciesNameFromAppointment = (
    appointment: AppointmentDailyHuddleFieldsFragment,
): string | null | undefined =>
    appointment.patient?.clinic_species?.description ||
    appointment.new_patient_species

export const getServiceNameFromAppointment = (
    appointment: Pick<AppointmentFieldsFragment, 'service' | 'service_name'>,
): string | null | undefined =>
    appointment.service?.name || appointment.service_name

export const getBreedFromPatient = (
    patient: Pick<PatientFieldsFragment, 'clinic_breed'> | null | undefined,
): string | null | undefined => patient?.clinic_breed?.description

export const getClientName = (
    client:
        | Pick<ClientFieldsFragment, 'first_name' | 'last_name'>
        | null
        | undefined,
) => getClientFullName(client, true) || ''
